import { useContext, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import styled from 'styled-components'

import {
  ContentWrapper,
  HideAllButton,
  MainContent,
  OptionList,
  QuestionBody,
  QuestionPanel,
  QuestionPanelHeader,
  QuestionTitle
} from '../styles'
import { QuestionnaireContext } from 'components/Assessment/QuestionnaireContext'

import HtmlParser from 'components/Assessment/HtmlParser'
import { AppCookies } from 'util/constants'
import Cookies from 'js-cookie'
import Choice from 'components/Assessment/Choice2'
import StimulusArea from 'components/StimulusArea/StimulusArea'
import { useAppSelector } from 'app/hooks'
import { SelectAssignmentState } from 'app/reducer/assignmentSlice'
import {
  getQuestionChoiceOptions,
  isChoiceEmpty,
  QuestionChoiceOption
} from '../utils'
import Highlighter from 'assets/img/highlighter.svg'
import Eraser from 'assets/img/eraser.svg'
import RightExpand from 'assets/icons/right-expand.svg'
import LeftExpand from 'assets/icons/left-expand.svg'
import { ViewMode } from 'interfaces/app'
import {
  TabHeaderActionButton,
  TabHeaderActionIcon
} from 'components/StimulusArea/styles'

type MainContentSectionProps = {
  activeTab: 'tab1' | 'tab2'
  questionVisible: boolean
  setActiveTab: any
  setQuestionVisible: any
}

const QuestionChoicesPanel = styled.div.attrs({
  className: 'questions__question-item cms-content-area'
})``

const MainContentSection = ({
  activeTab,
  questionVisible,
  setActiveTab,
  setQuestionVisible
}: MainContentSectionProps) => {
  const [searchParams] = useSearchParams()
  const {
    currentQuestion,
    hasPassage,
    onAnswerChange,
    isHighlighterActive,
    showHighlightEraser,
    isPausedByTeacher,
    viewMode
  } = useContext(QuestionnaireContext)
  const { enableStudentSubmissionView } = useAppSelector(SelectAssignmentState)
  const showAnswers = searchParams.get('showAnswers') !== 'false'

  const [hiddenChoices, setHiddenChoices] = useState<Record<string, boolean>>(
    {}
  )
  /**
   * Benchmark and formative panels match assignment behavior: either the
   * stimulus or question can fill the viewer, and the next arrow restores
   * the standard split layout without changing assessment state.
   */
  const [stimulusVisible, setStimulusVisible] = useState(true)
  /**
   * Benchmark review reuses the existing student mode query flag, while the
   * standard assessment launch omits it and should stay interactive.
   */
  const isReadOnlyViewMode = viewMode === ViewMode.Student

  const {
    id: currentQuestionId,
    passage_directions,
    passage_1,
    direction,
    passage_2,
    answer,
    question,
    choice_1,
    choice_2,
    choice_3,
    choice_4,
    student_answer,
    hasPassage1,
    hasPassage2
  } = currentQuestion

  /**
   * Each assessment question starts from the normal split layout so questions
   * with fewer controls cannot inherit a hidden state they cannot reverse.
   */
  useEffect(() => {
    setQuestionVisible(true)
    setStimulusVisible(true)
  }, [currentQuestionId, setQuestionVisible])

  const questionChoices = getQuestionChoiceOptions({
    choice_1,
    choice_2,
    choice_3,
    choice_4
  })
  /**
   * Student assessment view should follow the same authored-html emptiness rule as CMS.
   * Empty rich-text shells are skipped so blank choices never render as answer options.
   */
  const visibleQuestionChoices = questionChoices.filter(
    (choice) => !isChoiceEmpty(choice.value)
  )
  const areAllChoicesHidden =
    visibleQuestionChoices.length > 0 &&
    visibleQuestionChoices.every(
      (choice) => hiddenChoices[`${currentQuestionId}-${choice.key}`]
    )

  /**
   * Keep bulk visibility and individual choice toggles in one map. The button
   * text is derived from that map so it updates after either interaction.
   */
  const setChoicesHidden = (
    choices: QuestionChoiceOption[],
    hidden: boolean
  ) => {
    setHiddenChoices((prev) => ({
      ...prev,
      ...choices.reduce<Record<string, boolean>>(
        (nextChoices, choice) => ({
          ...nextChoices,
          [`${currentQuestionId}-${choice.key}`]: hidden
        }),
        {}
      )
    }))
  }

  const shouldShowPassageTabs = hasPassage || hasPassage1 || hasPassage2

  /**
   * Each arrow is disabled when its transition would not change the currently
   * visible panels.
   */
  const isLeftArrowDisabled = questionVisible && !stimulusVisible
  const isRightArrowDisabled = !questionVisible && stimulusVisible

  function onLeftArrowClick() {
    if (isLeftArrowDisabled) return

    if (questionVisible && stimulusVisible) {
      return setStimulusVisible(false)
    }

    if (!questionVisible && stimulusVisible) {
      setQuestionVisible(true)
    }
  }

  function onRightArrowClick() {
    if (isRightArrowDisabled) return

    if (questionVisible && !stimulusVisible) {
      setStimulusVisible(true)
    }

    if (questionVisible && stimulusVisible) {
      setQuestionVisible(false)
    }
  }

  const isMath = Cookies.get(AppCookies.isMath) === 'true'

  return (
    <MainContent
      id='highlight'
      style={{
        cursor: isHighlighterActive
          ? `url(${Highlighter}) 0 35, move`
          : showHighlightEraser
            ? `url(${Eraser}) 14 41, move`
            : ''
      }}
    >
      <div className='mainContent'>
        {isPausedByTeacher && (
          <div>
            <div className='mainContent-backDrop'></div>
            <div className='mainContent-alert loading'>
              <p>
                Assessment has been paused by instructor <span>.</span>
                <span>.</span>
                <span>.</span>
              </p>
            </div>
          </div>
        )}
        <div
          className={`mainContent-header ${isMath ? 'changing-width' : ''}`}
        />
      </div>

      <div
        className='flex'
        id='sketchContainerELA'
        style={{
          position: 'relative',
          flexDirection: 'column',
          width: '100%',
          height: '100%'
        }}
      >
        <ContentWrapper>
          {shouldShowPassageTabs && stimulusVisible && (
            <StimulusArea
              readOnly={true}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              fullWidth={!questionVisible}
              stimulusTabOne={passage_1 || ''}
              stimulusTabTwo={passage_2 || ''}
              stimulusTabOneId='passage_1'
              stimulusTabTwoId='passage_2'
              onLeftArrowClick={onLeftArrowClick}
              onRightArrowClick={onRightArrowClick}
              isLeftArrowDisabled={isLeftArrowDisabled}
              isRightArrowDisabled={isRightArrowDisabled}
              instructionHtml={passage_directions || 'passage_directions'}
            />
          )}
          {questionVisible && (
            <QuestionPanel
              $fullWidth={!stimulusVisible || !hasPassage}
              className='contrast-area'
            >
              {!stimulusVisible && (
                <div style={{ paddingLeft: 10, paddingRight: 10 }}>
                  <TabHeaderActionButton style={{ position: 'relative' }}>
                    <TabHeaderActionIcon
                      $disabled={isRightArrowDisabled}
                      aria-disabled={isRightArrowDisabled}
                      onClick={
                        isRightArrowDisabled ? undefined : onRightArrowClick
                      }
                      src={RightExpand}
                      alt='maximize'
                    />
                    <TabHeaderActionIcon
                      $disabled={isLeftArrowDisabled}
                      aria-disabled={isLeftArrowDisabled}
                      onClick={
                        isLeftArrowDisabled ? undefined : onLeftArrowClick
                      }
                      src={LeftExpand}
                      alt='maximize'
                    />
                  </TabHeaderActionButton>
                </div>
              )}
              <span>
                <span className='contrast-area-secondary'>
                  <div className='questions__dialogue'>
                    <p>
                      <HtmlParser html={direction || ''} id='direction' />
                    </p>
                  </div>
                </span>
                <QuestionPanelHeader>
                  <QuestionTitle>
                    <HtmlParser html={question || ''} id='question' />
                  </QuestionTitle>
                </QuestionPanelHeader>
                <QuestionBody>
                  {!isReadOnlyViewMode && (
                    <HideAllButton
                      type='button'
                      disabled={enableStudentSubmissionView}
                      onClick={() =>
                        setChoicesHidden(
                          visibleQuestionChoices,
                          !areAllChoicesHidden
                        )
                      }
                    >
                      {areAllChoicesHidden ? 'Show All' : 'Hide All'}
                    </HideAllButton>
                  )}

                  <OptionList>
                    {visibleQuestionChoices.map((option) => (
                      <QuestionChoicesPanel
                        key={`${currentQuestionId}-${option.key}`}
                        className='questions__question-item'
                        role={isReadOnlyViewMode ? undefined : 'button'}
                        tabIndex={isReadOnlyViewMode ? -1 : 0}
                      >
                        <Choice
                          option={option.answerValue}
                          choiceId={option.key}
                          choiceText={option.value}
                          onClick={() => {
                            /**
                             * Read only benchmark review keeps the answer rows
                             * visible for feedback but disables selection changes.
                             */
                            if (isReadOnlyViewMode) return
                            onAnswerChange(option.answerValue)
                          }}
                          answer={answer}
                          {...(isReadOnlyViewMode &&
                          typeof student_answer === 'number'
                            ? {
                                studentAnswer: student_answer
                              }
                            : {})}
                          disable={isReadOnlyViewMode}
                          showIcons
                          choice={option.code}
                          {...(showAnswers ? { preview: true } : {})}
                          hidden={Boolean(
                            hiddenChoices[`${currentQuestionId}-${option.key}`]
                          )}
                          onHiddenChange={(hidden) =>
                            setHiddenChoices((prev) => ({
                              ...prev,
                              [`${currentQuestionId}-${option.key}`]: hidden
                            }))
                          }
                        />
                      </QuestionChoicesPanel>
                    ))}
                  </OptionList>
                </QuestionBody>
              </span>
            </QuestionPanel>
          )}
        </ContentWrapper>
      </div>
    </MainContent>
  )
}

export default MainContentSection
